import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import { fetchLocation } from 'src/api/booking';
import { fetchShipmentInstructionsPartiesAddress } from 'src/api/shipmentExecution';
import { BookingOffice } from 'src/components/pages/Enquiry/Modals/Booking.types';
import { ShipmentData } from 'src/components/pages/shipment-execution/SE.Types';
import { getSIPartyAddress } from 'src/components/pages/shipment-execution/SE.utils';
import { useDebounce } from 'src/hooks/useDebounce';
import {
  Checkbox,
  Col,
  DatePicker,
  Form,
  Icon,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Spin,
  TextArea,
} from 'src/Library/AntdLibComponents';
import {
  renderLabelAndInput, renderSelectDropDown,
} from 'src/Library/Common';
import { convertToLabelValueArray, isValidArray, isValidString, stops } from 'src/Library/Helper';
import { KeyLabel, StylesProps, UserData } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import { arrayToObject } from 'src/utils/utils';
import OrderDetailsIcon from '../../../create-shipment/create-shipment.svg/order-details';
import { uploadSIModalStyles } from './UploadSIModal.styles';
import { getMandatoryFields } from './UploadSIModal.utils';
import UploadSINotifyPartiesAddAddress from './UploadSINotifyPartiesAddAddress';
import Asterisk from 'src/components/common/Asterisk';

interface IProps extends StylesProps<ReturnType<typeof uploadSIModalStyles>> {
  form;
  chargeTypes: KeyLabel[];
  payer: KeyLabel[];
  freightTerms: KeyLabel[];
  documentTypes: KeyLabel[];
  filingStatus: KeyLabel[];
  customComplianceParties: string[];
  referenceNumbersList: KeyLabel[];
  prefillingData;
  shipmentData: ShipmentData;
  setAddressList: (addressList) => void;
  addressList: any;
  countryId: string;
  SICountryValidations;
  userData: UserData;
}

interface StateProps {
  paymentDetails;
  referenceNumbers;
  notifyUsers;
  partnerNotifyUsers;
  userFieldChanged?: boolean;
  userFieldIndex?: number;
  isAddEditAddressModalVisible?: boolean;
  editAddressData?;
  isFetchingAddress: boolean;
  selectedPartyType?: string;
  compliancePartyFieldChanged?: boolean;
  compliancePartyFieldIndex?: number;
  selectedFilingStatus: string;
  blReleaseOffice: BookingOffice[];
  isblReleaseOfficeLoading: boolean;
  searchTerm: string;
}

const UploadSIModalAdditionalDetails = (props: IProps): React.ReactElement => {

  const {
    classes,
    form,
    chargeTypes,
    freightTerms,
    payer,
    documentTypes,
    filingStatus,
    customComplianceParties,
    referenceNumbersList,
    prefillingData,
    shipmentData,
    setAddressList,
    addressList,
    countryId,
    SICountryValidations,
    userData,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    paymentDetails: isValidArray(prefillingData?.paymentDetails) ? prefillingData?.paymentDetails : [{}],
    referenceNumbers: isValidArray(prefillingData?.referenceNumbers) ? prefillingData?.referenceNumbers : [{}],
    notifyUsers: isValidArray(prefillingData?.notifyUsers) ? prefillingData?.notifyUsers : [{
      name: userData.name,
      email: userData.email,
    }],
    partnerNotifyUsers: isValidArray(prefillingData?.partnerNotifyUsers) ? prefillingData?.partnerNotifyUsers : [{}],
    isFetchingAddress: false,
    selectedFilingStatus: prefillingData?.customsCompliance?.filingStatus || 'Carrier',
    blReleaseOffice: [],
    isblReleaseOfficeLoading: false,
    searchTerm: '',
  });

  const {
    paymentDetails,
    referenceNumbers,
    notifyUsers,
    partnerNotifyUsers,
    userFieldIndex,
    userFieldChanged,
    isAddEditAddressModalVisible,
    editAddressData,
    isFetchingAddress,
    selectedPartyType,
    compliancePartyFieldIndex,
    compliancePartyFieldChanged,
    selectedFilingStatus,
    blReleaseOffice,
    isblReleaseOfficeLoading,
    searchTerm,
  } = state;

  const {
    getFieldDecorator,
    getFieldValue,
    getFieldsValue,
    setFieldsValue,
  } = form;

  const mandatoryFields = getMandatoryFields({
    SICountryValidations,
    countryId,
    shipmentData
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  React.useEffect(() => {
    fetchBlReleaseOffice(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  React.useEffect(() => {
    form.validateFields([
      `notifyUsers[${userFieldIndex}].name`,
      `notifyUsers[${userFieldIndex}].email`,
      `partnerNotifyUsers[${userFieldIndex}].name`,
      `partnerNotifyUsers[${userFieldIndex}].email`,
    ], (err, values) => {
      if (err) return;
    });
  }, [userFieldChanged]);

  React.useEffect(() => {
    form.validateFields([
      `customsCompliance.parties[${compliancePartyFieldIndex}].name`,
      `customsCompliance.parties[${compliancePartyFieldIndex}].address`,
    ], (err, values) => {
      if (err) return;
    });
  }, [compliancePartyFieldChanged]);

  React.useEffect(() => {
    setFieldsValue({ ['customsCompliance.filingStatus']: selectedFilingStatus });
  }, [selectedFilingStatus])

  const onChangePaymentField = (option, index, key) => {
    if (key === 'payer') {
      if (option === 'Shipper') {
        form.setFieldsValue({ [`paymentDetails[${index}].name`]: shipmentData?.shipperName })
      } else if (option === 'Forwarder') {
          form.setFieldsValue({ [`paymentDetails[${index}].name`]: shipmentData?.ffName })
      } else if (option === 'Consignee' || !option) {
        form.setFieldsValue({ [`paymentDetails[${index}].name`]: undefined })
      }
    }
  };

  const isPaymentOrUserFieldMandatory = (index, key) => {
    const arrayList = isValidArray(form.getFieldValue(key))
      ? form.getFieldValue(key) : [{}];
    return isValidArray(arrayList) && arrayList[index] &&
      Object.values(arrayList[index])?.some(value => isValidString(value));
  };

  const renderPaymentFields =  (value, index, key, arrayList, initialValue?) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: 0, paddingRight: '8px' }}>
        {getFieldDecorator(`paymentDetails[${index}].${key}`, {
          rules: [{ required: true, message: 'Required' }],
          initialValue,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            allowClear
            className={classes.selectField}
            placeholder={`Select ${value}`}
            dropdownMatchSelectWidth={false}
            showSearch
            optionFilterProp="children"
            onChange={(option) => {
              onChangePaymentField(option, index, key)
            }}
          >
            {arrayList?.map(item => (
              <Select.Option key={item.key}>{item.label}</Select.Option>
            ))}
          </Select>
        )}
      </Form.Item>
    );
  };

  const getInitialValues= (index, key, state) => {
    if ((!isEmpty(state) && state[index])) {
      return state[index][key];
    }
    return undefined;
  };

  const handleRemove = (index, label) => {
    const newList = form.getFieldValue(label);
    newList.splice(index, 1);
    setState({ [`${label}`]: newList });
    form.setFieldsValue({
      [`${label}`]: newList,
    });
  };

  const handleAddMore = (label, list) => {
    const newList = [
      ...list,
      {},
    ];
    setState({ [`${label}`]: newList });
  };

  const renderPaymentDetails = () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>
          <div className={classes.boxIcon}>
            <OrderDetailsIcon  className={classes.boxIcon}/>
          </div>
          Payment <br/> Details*
        </Col>
        <Col span={13}>
          <div>
            <Row style={{ fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>
              <Col span={6}>
                Charge Type
              </Col>
              <Col span={6}>
                Freight Terms
              </Col>
              <Col span={5}>
                Payer
              </Col>
              <Col span={6}>
                Payer Org Name
              </Col>
            </Row>
          </div>
          <div>
            {paymentDetails?.map((item, index) => (
              <Row className={classes.paymentDetails} key={index}>
                <Col span={6}>
                  {renderPaymentFields(
                    'Charge Type',
                    index,
                    'chargeType',
                    chargeTypes,
                    getInitialValues(index, 'chargeType', paymentDetails),
                  )}
                </Col>
                <Col span={6}>
                  {renderPaymentFields(
                    'Freight Terms',
                    index,
                    'freightTerms',
                    freightTerms,
                    getInitialValues(index, 'freightTerms', paymentDetails),
                  )}
                </Col>
                <Col span={5}>
                  {renderPaymentFields(
                    'Payer',
                    index,
                    'payer',
                    payer,
                    getInitialValues(index, 'payer', paymentDetails),
                  )}
                </Col>
                <Col span={6}>
                <Form.Item style={{ marginBottom: 0 }}>
                  {form.getFieldDecorator(`paymentDetails[${index}].name`, {
                    rules: [{ required: true, message: 'Required' }],
                    initialValue: getInitialValues(index, 'name', paymentDetails),
                  })(
                    <Input
                      placeholder="Name"
                      className={classes.inputField}
                    />
                  )}
                </Form.Item>
                </Col>
                <Col span={1} style={{ textAlign: 'right' }}>
                  {(paymentDetails?.length !== 1 && index !== paymentDetails?.length - 1) && <Icon type="close"
                    style={{ color: 'red', marginTop: '12px' }}
                    onClick={() => handleRemove(index, 'paymentDetails')}
                  />}
                </Col>
              </Row>
            ))}
            <div
              className={classes.addMore}
              onClick={() => handleAddMore('paymentDetails', paymentDetails)}
            >+ Add More</div>
          </div>
        </Col>
      </Row>
    );
  };

  const renderBLCopyField = (key, initialValue?) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {getFieldDecorator(key, {
          initialValue,
        })(
          <InputNumber
            placeholder="Enter number of Copies"
            className={classes.inputNumberField}
            precision={0}
            min={0}
          />
        )}
      </Form.Item>
    );
  };

  const fetchBlReleaseOffice = async (queryString = '') => {
    setState({ isblReleaseOfficeLoading: true });
    let blReleaseOffice = [];
    const response = await fetchLocation({ queryString });
    if (response.isSuccess) {
      blReleaseOffice = response.data || [];
    } else {
      message.error(response.errorMessage);
    }
    setState({
      blReleaseOffice,
      isblReleaseOfficeLoading: false,
    });
  };

  const renderBLInstructions = () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>BL Instructions</Col>
        <Col span={10}>
          <Row className={classes.flexRow}>
            <Col span={5} className={classes.contentLabel}>Document Type</Col>
            <Col span={19}>
            <Form.Item style={{ marginBottom: 0 }}>
              {getFieldDecorator('blInstructions.documentType', {
                initialValue: prefillingData?.blInstructions?.documentType || 'BillOfLadingOriginal',
              })(
                <Radio.Group>
                  {documentTypes?.map(item  => (
                    <Radio value={item.key} style={{ marginRight: '12px', fontSize: '12px' }} key={item.key}>
                      {item.label}
                    </Radio>
                  ))}
                </Radio.Group>
              )}
            </Form.Item>
            </Col>
          </Row>
          <Row className={classes.flexRow} style={{ marginTop: '16px' }}>
            <Col span={5} className={classes.contentLabel} />
            <Col span={8} className={classes.contentLabel} style={{ paddingLeft: '12px' }}>Freighted</Col>
            <Col span={8} className={classes.contentLabel} style={{ paddingLeft: '12px' }}>Unfreighted</Col>
          </Row>
          <Row className={classes.flexRow}>
            <Col span={5} className={classes.contentLabel} />
            <Col span={8} style={{ paddingLeft: '12px' }}>
              {renderBLCopyField(
                'blInstructions.freighted.default',
                prefillingData?.blInstructions?.freighted?.default,
              )}
            </Col>
            <Col span={8} style={{ paddingLeft: '12px' }}>
              {renderBLCopyField(
                'blInstructions.unfreighted.default',
                prefillingData?.blInstructions?.unfreighted?.default || 3,
              )}
            </Col>
          </Row>
          {getFieldValue('blInstructions.documentType') === 'BillOfLadingOriginal' &&
            <Row className={classes.flexRow} style={{ marginTop: '12px' }}>
              <Col span={5} className={classes.contentLabel} style={{ textAlign: 'right' }}>
                Non-Negotiable
              </Col>
              <Col span={8} style={{ paddingLeft: '12px' }}>
                {renderBLCopyField(
                  'blInstructions.freighted.nonNegotiable',
                  prefillingData?.blInstructions?.freighted?.nonNegotiable,
                )}
              </Col>
              <Col span={8} style={{ paddingLeft: '12px' }}>
                {renderBLCopyField(
                  'blInstructions.unfreighted.nonNegotiable',
                  prefillingData?.blInstructions?.freighted?.nonNegotiable || 3,
                )}
              </Col>
            </Row>
          }
        </Col>
        <Col span={1} />
        <Col span={9}>
          <Row className={classes.flexRow}>
            <Col span={6} className={classes.contentLabel}>BL Release Date</Col>
            <Col span={8}>
            <Form.Item style={{ marginBottom: 0 }}>
              {getFieldDecorator('blInstructions.blReleaseDate', {
                initialValue: prefillingData?.blInstructions?.blReleaseDate,
              })(
                <DatePicker
                  className={classes.datePicker}
                  placeholder="BL Release Date"
                  format="DD MMM YYYY"
                />
              )}
            </Form.Item>
            </Col>
          </Row>
          <Row className={classes.flexRow} style={{ marginTop: '16px' }}>
            <Col span={6} className={classes.contentLabel}>BL Release Office</Col>
            <Col span={12}>
              <Form.Item style={{ marginBottom: 0 }}>
              {getFieldDecorator('blInstructions.blReleaseOfficeId', {
                initialValue: prefillingData?.blInstructions?.blReleaseOfficeId,
              })(
                <Select
                  getPopupContainer={(trigger: any) => trigger.parentNode}
                  allowClear
                  labelInValue
                  showSearch
                  className={classes.selectField}
                  onFocus={() => setState({ searchTerm: '' })}
                  onSearch={(searchTerm) => setState({ searchTerm })}
                  placeholder="BL Release Office"
                  filterOption={(inputValue, option) => true}
                  dropdownRender={menu => renderSelectDropDown(menu, isblReleaseOfficeLoading)}
                >
                  {blReleaseOffice?.map(item => (
                    <Select.Option key={item.id}>{`${item.name} (${item.code})`}</Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderCustomsCompliance = () : React.ReactElement => {
    const labelInputProps = {
      colSpan: {
        left: 6,
        right: 15,
      },
      form,
      classes,
    };
    const {
      isShipperTaxIdMandatory,
      isCarrierTaxIdMandatory,
      isFilingStatusMandatory,
    } = mandatoryFields;

    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>Customs Compliance</Col>
        <Col span={20}>
          <Row>
            <Col span={10}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: <>Shipper Tax ID{isShipperTaxIdMandatory ? <Asterisk /> : null}</>,
                key: 'customsCompliance.shipperTaxId',
                initialValue: prefillingData?.customsCompliance?.shipperTaxId,
                placeholder: 'Shipper Tax ID',
                rules: [{ required: isShipperTaxIdMandatory, message: 'Required' }],
              })}
            </Col>
            <Col span={1} />
            <Col span={10}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: <>Carrier Tax ID{isCarrierTaxIdMandatory ? <Asterisk /> : null}</>,
                key: 'customsCompliance.carrierTaxId',
                initialValue: prefillingData?.customsCompliance?.carrierTaxId,
                placeholder: 'Carrier Tax ID',
                rules: [{ required: isCarrierTaxIdMandatory, message: 'Required' }],
              })}
            </Col>
          </Row>
          <Row className={classes.flexRow}>
            <Col span={2} className={classes.contentLabel} style={{ color: '#303030' }}>
              Filing Status{isFilingStatusMandatory ? <Asterisk /> : null}
            </Col>
            <Col span={19} style={{ paddingLeft: '20px' }}>
              <Form.Item style={{ marginBottom: 0 }}>
                {getFieldDecorator('customsCompliance.filingStatus')(
                  <Checkbox.Group
                    options={convertToLabelValueArray(filingStatus)}
                    style={{ marginRight: '12px', fontSize: '12px' }}
                    onChange={(val: string[]) => {
                      if (isValidArray(val)) {
                        setState({ selectedFilingStatus: val[0] });
                      } else {
                        setState({ selectedFilingStatus: undefined });
                      }
                    }}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
          {getFieldValue('customsCompliance.filingStatus') && renderCustomComplianceParties()}
        </Col>
      </Row>
    );
  };

  const referenceNumbersMap = arrayToObject(referenceNumbersList, 'key');

  const getRemainingReferenceNumbersAdded = () => {
    const referenceNumbersAdded = getFieldsValue()?.referenceNumbers;
    referenceNumbersAdded?.forEach(item => (
      delete referenceNumbersMap[item.type?.key]
    ));
    return referenceNumbersMap;
  };

  const renderReferenceNumbersSelect =  (key) : React.ReactElement => {
    const remainingReferenceNumbers = getRemainingReferenceNumbersAdded();
    const {
      mandatoryReferenceNumbers,
    } = mandatoryFields;
    return (
      <Form.Item style={{ marginBottom: 0, paddingRight: '8px' }}>
        {getFieldDecorator(`referenceNumbers[${key}].type`, {
          rules: [{ required: isValidArray(mandatoryReferenceNumbers), message: 'Required' }],
          initialValue: referenceNumbers && referenceNumbers[key]?.type,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            allowClear
            className={classes.selectField}
            placeholder="Select Reference Number"
            dropdownMatchSelectWidth={false}
            showSearch
            labelInValue
            optionFilterProp="children"
          >
            {remainingReferenceNumbers && Object.keys(remainingReferenceNumbers)?.map(key => (
              <Select.Option key={remainingReferenceNumbers[key].key}>
                {remainingReferenceNumbers[key].label}
              </Select.Option>
            ))}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderReferenceNumbers = () : React.ReactElement => {
    const {
      mandatoryReferenceNumbers,
    } = mandatoryFields;

    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading} style={{ marginTop: '8px' }}>
          Reference Numbers{isValidArray(mandatoryReferenceNumbers) ? <Asterisk /> : null}
        </Col>
        <Col span={20}>
          {referenceNumbers?.map((item, index) => (
            <Row key={index}>
              <Col span={5}>
                {renderReferenceNumbersSelect(index)}
              </Col>
              <Col span={4}>
                <Form.Item style={{ marginBottom: 0 }}>
                  {form.getFieldDecorator(`referenceNumbers[${index}].value`, {
                    rules: [
                      { required: isValidArray(mandatoryReferenceNumbers), message: 'Required' },
                      { max: 35, message: 'Maximum 35 characters allowed' },
                    ],
                    initialValue: referenceNumbers && referenceNumbers[index]?.value,
                  })(
                    <Input
                      className={classes.inputField}
                    />
                  )}
                </Form.Item>
              </Col>
              <Col span={1} style={{ textAlign: 'right' }}>
                {(referenceNumbers?.length !== 1 && index !== referenceNumbers?.length - 1) &&
                  <Icon type="close"
                    style={{ color: 'red', marginTop: '12px' }}
                    onClick={() => handleRemove(index, 'referenceNumbers')}
                  />}
              </Col>
            </Row>
          ))}
          <div
            className={classes.addMore}
            onClick={() => handleAddMore('referenceNumbers', referenceNumbers)}
          >+ Add More</div>
        </Col>
      </Row>
    );
  };

  const handleUserChange = (index) => {
    setState({
      userFieldIndex: index,
      userFieldChanged: !userFieldChanged,
    });
  };

  const renderNotifyUsersField =  (key, index, fieldKey, label, initialValue?) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {getFieldDecorator(`${key}[${index}].${fieldKey}`, {
          rules: [
            {
              validator: (rule, value, cb) => {
                if (!value && isPaymentOrUserFieldMandatory(index, key)) {
                  cb('Required');
                } else cb();
              },
            },
            ...((fieldKey === 'email') ? [{
            type: 'email', message: 'Please enter a valid Email Id',
            }] : []),
          ],
            initialValue,
        })(
          <Input
            className={classes.inputNumberField}
            placeholder={label}
            onChange={() => handleUserChange(index)}
          />
        )}
      </Form.Item>
    );
  };

  const renderNotifyUsers = () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>Notify Users</Col>
        <Col span={20}>
          <div className={classes.contentLabel} style={{ marginBottom: '8px' }}>Carrier Notifications</div>
          {renderNotifyUsersList('notifyUsers', notifyUsers)}
          <div className={classes.contentLabel} style={{ marginTop: '24px' }}>INTTRA Notifications</div>
          <Form.Item style={{ marginBottom: 0 }}>
            {getFieldDecorator('isDifferentPartnerNotifications', {
              initialValue: prefillingData?.isDifferentPartnerNotifications || false,
              valuePropName: 'checked',
            })(
              <Checkbox
                style={{ margin: '10px 0 8px 0' }}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setState({ partnerNotifyUsers: [{}] })
                  }
                }}
              >
                I want the Partner notification emails to be send to a different ID
              </Checkbox>
            )}
          </Form.Item>
          {getFieldValue('isDifferentPartnerNotifications') &&
            renderNotifyUsersList('partnerNotifyUsers', partnerNotifyUsers)}
        </Col>
      </Row>
    );
  };

  const renderNotifyUsersList = (key, arrayList) : React.ReactElement => {
    return (
      <React.Fragment>
        <div>
          <Row style={{ fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>
            <Col span={1}>
              S.N.
            </Col>
            <Col span={4}>
              Name
            </Col>
            <Col span={7} style={{ paddingLeft: '6px' }}>
              Email ID
            </Col>
            <Col span={1} />
          </Row>
        </div>
        <div>
          {arrayList?.map((item, index) => (
            <Row className={classes.paymentDetails} key={index}>
              <Col span={1} style={{ marginTop: '10px' }}>
                {index + 1}
              </Col>
              <Col span={4}>
                {renderNotifyUsersField(
                  key,
                  index,
                  'name',
                  'Name',
                  getInitialValues(index, 'name', arrayList),
                )}
              </Col>
              <Col span={7} style={{ paddingLeft: '6px' }}>
                {renderNotifyUsersField(
                  key,
                  index,
                  'email',
                  'Email',
                  getInitialValues(index, 'email', arrayList),
                )}
              </Col>
              <Col span={1} style={{ textAlign: 'right' }}>
                {(arrayList?.length !== 1 && index !== arrayList?.length - 1) &&
                  <Icon type="close"
                    style={{ color: 'red', marginTop: '12px' }}
                    onClick={() => handleRemove(index, key)}
                  />
                }
              </Col>
            </Row>
          ))}
          {arrayList?.length < 6 && <div
            className={classes.addMore}
            onClick={() => handleAddMore(key, arrayList)}
          >+ Add More</div>}
        </div>
      </React.Fragment>
    );
  };

  const renderBLComments = () : React.ReactElement => {
    return (
      <Row className={classes.box} style={{ borderBottom: 'unset', marginBottom: 65 }}>
        <Col span={4} className={classes.contentHeading}>BL Comments</Col>
        <Col span={10}>
          <Form.Item style={{ marginBottom: 0 }}>
            {form.getFieldDecorator('blComments', {
              initialValue: prefillingData?.blComments,
            })(
              <TextArea
                rows={3}
                placeholder="BL Comments"
                className={classes.textAreaField}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const getAddressList = async (index, callApi?) : Promise<void> => {
    // in custom compiance Shipper is the first element and then Consignee hence the index check
    const partyType = index === 0 ? 'Shipper' : 'Consignee';
    if (!addressList || callApi) {
      const response = await fetchShipmentInstructionsPartiesAddress({ shipmentId: shipmentData?.id });
      if (response.isSuccess) {
        setAddressList(response?.data || []);
        setState({ isFetchingAddress: false });
        if (!callApi && (!isValidArray(response?.data && response.data[partyType]))) {
          setState({ isAddEditAddressModalVisible: true });
        }
      } else {
        message.error(response.errorMessage);
        setState({ isFetchingAddress: false });
      }
    } else if (!isValidArray(addressList[partyType])) {
      setState({ isAddEditAddressModalVisible: true });
    }
  };

  const getEditAddressData = (index) => {
    const selectedAddress = getFieldValue(`customsCompliance.parties[${index}].address`);
    const partyType = index === 0 ? 'Shipper' : 'Consignee';
    const addressData = addressList &&
      addressList[partyType]?.find(item => item.id === selectedAddress?.key);
    return addressData;
  };

  const renderComplianceAddress = (index) : React.ReactElement => {
    const partyType = index === 0 ? 'Shipper' : 'Consignee';
    return (
      <Form.Item style={{ marginBottom: 0 }}>
        {form.getFieldDecorator(`customsCompliance.parties[${index}].address`, {
          rules: [
            {
              validator: (rule, value, cb) => {
                if (!value && isComplianceNameMandatory(index)) {
                  cb('Required');
                } else cb();
              },
            },
          ],
          initialValue: (prefillingData?.customsCompliance?.parties?.[index]?.address) ||
            ((index === 0 && addressList && addressList.Shipper && addressList.Shipper[0]) ?
            {
              key: addressList.Shipper[0].id,
              label: getSIPartyAddress(addressList.Shipper[0]),
            } : undefined),
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classNames(classes.selectField, classes.suffixIcon)}
            showSearch
            allowClear
            labelInValue
            showArrow={isValidString(getFieldValue(`customsCompliance.parties[${index}].address`)?.key)}
            optionFilterProp="children"
            placeholder="Select Address"
            suffixIcon={
              <Icon
                type="edit"
                onClick={() => setState({
                  isAddEditAddressModalVisible: true,
                  editAddressData: getEditAddressData(index),
                })}
                theme="filled"
              />
            }
            onFocus={() => {
              getAddressList(index);
              setState({ selectedPartyType: partyType });
            }}
            dropdownRender={menu => {
              if (addressList && isValidArray(addressList[partyType])) {
                return renderAddressDropdown(menu);
              } else {
                return <div></div>;
              }
            }}
            onChange={() => {
              setState({
                compliancePartyFieldIndex: index,
                compliancePartyFieldChanged: !compliancePartyFieldChanged,
              });
            }}
          >
            {addressList && addressList[partyType]?.map(item => (
              <Select.Option key={item.id}>{getSIPartyAddress(item)}</Select.Option>
            ))}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderAddressDropdown = (menu: React.ReactNode) => {
    if (isFetchingAddress) return <Spin style={{ marginLeft: '15px' }} />;
    return <React.Fragment>
      {menu}
      <div
        className={classes.createAddressStyle}
        onMouseDown={e => stops(e)}
        onClick={() => setState({ isAddEditAddressModalVisible: true })}>
        + Add Address
      </div>
    </React.Fragment>;
  };

  const isComplianceNameMandatory = (index) => {
    const {
      mandatoryCustomsComplianceFields,
    } = mandatoryFields;
    if (mandatoryCustomsComplianceFields[index === 0 ? 'Shipper' : 'Consignee']) return true;
    const arrayList = isValidArray(form.getFieldValue('customsCompliance.parties'))
      ? form.getFieldValue('customsCompliance.parties') : [{}];
    if (isValidArray(arrayList)) {
      return !isEmpty(arrayList[index]?.address) || isValidString(arrayList[index]?.name);
    }
    return;
  }

  const renderCustomComplianceParties = () : React.ReactElement => {
    const isFilingStatusSelf = getFieldValue('customsCompliance.filingStatus') === 'Self';
    const labelInputProps = {
      colSpan: {
        left: 6,
        right: 15,
      },
      form,
      classes,
    };
    const {
      mandatoryCustomsComplianceFields,
    } = mandatoryFields;
    return (
      <Row style={{ marginTop: '12px' }}>
        <Col span={isFilingStatusSelf ? 10 : 22} style={{ backgroundColor: '#FAFAFA', padding: '16px' }}>
          {isFilingStatusSelf &&
            renderLabelAndInput({
              ...labelInputProps,
              label: 'SCAC Code*',
              key: 'customsCompliance.filerScacCode',
              initialValue: prefillingData?.customsCompliance?.filerScacCode,
              placeholder: 'SCAC Code',
              rules: [
                { required: true, message: 'Required' },
                { max: 35, message: 'Maximum 35 chracters allowed' },
              ],
              formStyles: { marginBottom: 0 },
            })
          }
          {getFieldValue('customsCompliance.filingStatus') === 'Carrier' &&
          <React.Fragment>
            <Row style={{ fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>
              <Col span={4} />
              <Col span={6} className={classes.contentLabel}>
                Name
              </Col>
              <Col span={14} className={classes.contentLabel} style={{ paddingLeft: '16px' }}>
                Address
              </Col>
            </Row>
            {customComplianceParties?.map((item, index) => (
              <Row className={classes.partiesInvolvedRow} key={index}>
                <Col span={4} className={classes.label}>
                  {item}{mandatoryCustomsComplianceFields[index === 0 ? 'Shipper' : 'Consignee'] ? <Asterisk /> : null}
                </Col>
                <Col span={6}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    {form.getFieldDecorator(`customsCompliance.parties[${index}].name`, {
                      rules: [
                        {
                          validator: (rule, value, cb) => {
                            if (!value && isComplianceNameMandatory(index)) {
                              cb('Required');
                            } else cb();
                          },
                        },
                      ],
                      initialValue: (prefillingData?.customsCompliance?.parties?.[index]?.name) ||
                        (index === 0 ? shipmentData?.shipperName : undefined),
                    })(
                      <Input
                        placeholder="Enter Name"
                        className={classes.inputField}
                        onChange={() => {
                          setState({
                            compliancePartyFieldIndex: index,
                            compliancePartyFieldChanged: !compliancePartyFieldChanged,
                          });
                        }}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col span={14} style={{ paddingLeft: '16px' }}>
                  {renderComplianceAddress(index)}
                </Col>
              </Row>
            ))}
          </React.Fragment>}
        </Col>
      </Row>
    );
  };

  const renderAddEditPartyAddress = () : React.ReactElement =>{
    return (
      <UploadSINotifyPartiesAddAddress
        isVisible={isAddEditAddressModalVisible}
        onCancel={() => {
          setState({
            isAddEditAddressModalVisible: false,
            editAddressData: undefined,
          })
        }}
        onSubmit={() => {
          getAddressList(0, true);
          setState({
            isAddEditAddressModalVisible: false,
            editAddressData: undefined,
          })
        }}
        editAddressData={editAddressData}
        shipmentId={shipmentData?.id}
        partyType={selectedPartyType}
      />
    );
  };

  return (
    <React.Fragment>
      {renderPaymentDetails()}
      {renderBLInstructions()}
      {renderCustomsCompliance()}
      {renderReferenceNumbers()}
      {renderNotifyUsers()}
      {renderBLComments()}
      {isAddEditAddressModalVisible && renderAddEditPartyAddress()}
    </React.Fragment>
  );
};

const mapStateToProps = ({ masterData }, ownProps) => ({
  chargeTypes: masterData.shippingInstructionsMasterData?.chargeTypes,
  freightTerms: masterData.shippingInstructionsMasterData?.freightTerms,
  payer: masterData.shippingInstructionsMasterData?.payer,
  documentTypes: masterData.shippingInstructionsMasterData?.documentTypes,
  filingStatus: masterData.shippingInstructionsMasterData?.filingStatus,
  customComplianceParties: masterData.shippingInstructionsMasterData?.customComplianceParties,
  referenceNumbersList: masterData.shippingInstructionsMasterData?.referenceNumbers,
  SICountryValidations: masterData.shippingInstructionsMasterData?.SICountryValidations,
  userData: masterData.userData,
});

export default connect(
  mapStateToProps,
  null,
  )(React.memo(withStyles(uploadSIModalStyles)(UploadSIModalAdditionalDetails)));